import BaseEntity from '../base/BaseEntity';
import Filter from '../base/filter/Filter';
import InputFilter from '../base/filter/InputFilter';
import SortFilter from '../base/filter/SortFilter';
import CheckFilter from '../base/filter/CheckFilter';
import SelectionFilter from '../base/filter/SelectionFilter';
import User from '../user/User';
import SafeUtil from '../../util/SafeUtil';
import EnvUtil from '../../util/EnvUtil';
import DateUtil from '../../util/DateUtil';
import Lang from '../global/Lang';

// 模块常量
export enum SystemLogModule {
  AUTH = 'auth',
  USER = 'user',
  DEPARTMENT = 'department',
  SPACE = 'space',
  SYSTEM = 'system',
  ROLE = 'role',
  LOG = 'log',
  BACKUP = 'backup',
  ALARM = 'alarm',
  // 添加缺失的模块类型
  SHARE = 'share',
  SYNC = 'sync',
  OTHER = 'other',
  FILE = 'file',
  SECURITY = 'security',
  STORAGE = 'storage'
}

// 操作类型常量
export enum SystemLogOperation {
  LOGIN = 'login',
  LOGOUT = 'logout',
  LOGIN_FAILED = 'login_failed',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  ENABLE = 'enable',
  DISABLE = 'disable',
  RESET_PASSWORD = 'reset_password',
  CHANGE_PASSWORD = 'change_password',
  IMPORT = 'import',
  EXPORT = 'export',
  BACKUP = 'backup',
  RESTORE = 'restore',
  CLEAN = 'clean',
  CONFIG_UPDATE = 'config_update',
  ROLE_ASSIGN = 'role_assign',
  PERMISSION_GRANT = 'permission_grant',
  PERMISSION_REVOKE = 'permission_revoke',
  // 添加缺失的操作类型
  BATCH_CREATE = 'batch_create',
  BATCH_DELETE = 'batch_delete',
  BATCH_UPDATE = 'batch_update',
  TRANSFIGURATION = 'transfiguration',
  SCAN = 'scan',
  SYNC = 'sync',
  EXECUTE = 'exec'
}

// 目标类型常量
export enum SystemLogTargetType {
  USER = 'user',
  DEPARTMENT = 'department',
  SPACE = 'space',
  SHARE = 'share',                    // 新增：分享
  SPACE_MEMBER = 'space_member',      // 新增：空间成员
  SYSTEM = 'system',
  ROLE = 'role',
  LOG = 'log',
  BACKUP = 'backup',
  SETTING = 'setting',
  SYSTEMTASK = 'system_task'
}

// 日志类型常量
export enum SystemLogType {
  NORMAL = 'normal',
  WARNING = 'warning',
  ERROR = 'error',
  SECURITY = 'security',
}

// 报警级别常量
export enum SystemLogAlarmLevel {
  NONE = 0,
  WARNING = 1,
  ERROR = 2,
  CRITICAL = 3
}

export default class SystemLog extends BaseEntity {
  userUuid: string = '';
  username: string = '';
  module: string = '';
  operation: string = '';
  targetType: string = '';
  targetUuid: string = '';
  targetName: string = '';
  detail: string = '';
  ip: string = '';
  userAgent: string = '';
  logType: string = 'normal';
  alarmLevel: number = 0;
  extraData: any = null;

  // 作为勾选变量
  check: boolean = false;
  // 模块显示文本
  moduleText: string = '';
  // 操作类型显示文本
  operationText: string = '';
  // 目标类型显示文本
  targetTypeText: string = '';
  // 日志类型显示文本
  logTypeText: string = '';
  // 报警级别显示文本
  alarmLevelText: string = '';

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  assign(obj: any) {
    super.assign(obj);
    this.assignEntity('createTime', Date);
    
    // 设置各字段的显示文本
    this.moduleText = this.getModuleText(this.module);
    this.operationText = this.getOperationText(this.operation);
    this.targetTypeText = this.getTargetTypeText(this.targetType);
    this.logTypeText = this.getLogTypeText(this.logType);
    this.alarmLevelText = this.getAlarmLevelText(this.alarmLevel);
    
    // 解析extraData
    if (typeof this.extraData === 'string' && this.extraData) {
      try {
        this.extraData = JSON.parse(this.extraData);
      } catch (e) {
        console.error('解析extraData失败:', e);
      }
    }
  }

  getTAG(): string {
    return 'systemLog';
  }
  getFormattedDetail(): string {
    if (!this.detail) return '';
    
    // 对于系统配置修改，可以美化显示
    if (this.module === SystemLogModule.SYSTEM && 
        this.operation === SystemLogOperation.UPDATE) {
      
      // 用换行符替换分号，使变更项逐行显示
      return this.detail.replace(/;/g, ';\n');
    }
    
    return this.detail;
  }
  // 提取变更项
extractChanges(): string[] {
  if (!this.hasChangeDetails()) return [];
  
  const parts = this.detail.split(': ');
  if (parts.length < 2) return [];
  
  const changesText = parts[1];
  return changesText.split('; ').filter(change => change.trim() !== '');
}
  // 判断是否包含变更详情
hasChangeDetails(): boolean {
  return this.detail.includes(': ') && this.detail.includes('→');
}
  getForm(): any {
    return {
      userUuid: this.userUuid,
      username: this.username,
      module: this.module,
      operation: this.operation,
      targetType: this.targetType,
      targetUuid: this.targetUuid,
      targetName: this.targetName,
      detail: this.detail,
      ip: this.ip,
      userAgent: this.userAgent,
      logType: this.logType,
      alarmLevel: this.alarmLevel,
      extraData: this.extraData ? JSON.stringify(this.extraData) : null,
    };
  }

// 获取模块的中文显示
getModuleText(module?: string): string {
  const targetModule = module || this.module;
  const moduleMap: { [key: string]: string } = {
    [SystemLogModule.AUTH]: Lang.t('systemLog.moduleAuth'),
    [SystemLogModule.USER]: Lang.t('systemLog.moduleUser'),
    [SystemLogModule.DEPARTMENT]: Lang.t('systemLog.moduleDepartment'),
    [SystemLogModule.SPACE]: Lang.t('systemLog.moduleSpace'),
    [SystemLogModule.SYSTEM]: Lang.t('systemLog.moduleSystem'),
    [SystemLogModule.ROLE]: Lang.t('systemLog.moduleRole'),
    [SystemLogModule.LOG]: Lang.t('systemLog.moduleLog'),
    [SystemLogModule.BACKUP]: Lang.t('systemLog.moduleBackup'),
    [SystemLogModule.ALARM]: Lang.t('systemLog.moduleAlarm'),
    // 添加缺失的模块类型映射
    [SystemLogModule.SHARE]: Lang.t('systemLog.moduleShare'),
    [SystemLogModule.SYNC]: Lang.t('systemLog.moduleSync'),
    [SystemLogModule.OTHER]: Lang.t('systemLog.moduleOther'),
    [SystemLogModule.FILE]: Lang.t('systemLog.moduleFile'),
    [SystemLogModule.SECURITY]: Lang.t('systemLog.moduleSecurity'),
    [SystemLogModule.STORAGE]: Lang.t('systemLog.moduleStorage'),
  };
  return moduleMap[targetModule] || targetModule;
}
// 获取操作类型的中文显示
getOperationText(operation?: string): string {
  const targetOperation = operation || this.operation;
  const operationMap: { [key: string]: string } = {
    [SystemLogOperation.LOGIN]: Lang.t('systemLog.operationLogin'),
    [SystemLogOperation.LOGOUT]: Lang.t('systemLog.operationLogout'),
    [SystemLogOperation.LOGIN_FAILED]: Lang.t('systemLog.operationLoginFailed'),
    [SystemLogOperation.CREATE]: Lang.t('systemLog.operationCreate'),
    [SystemLogOperation.UPDATE]: Lang.t('systemLog.operationUpdate'),
    [SystemLogOperation.DELETE]: Lang.t('systemLog.operationDelete'),
    [SystemLogOperation.ENABLE]: Lang.t('systemLog.operationEnable'),
    [SystemLogOperation.DISABLE]: Lang.t('systemLog.operationDisable'),
    [SystemLogOperation.RESET_PASSWORD]: Lang.t('systemLog.operationResetPassword'),
    [SystemLogOperation.CHANGE_PASSWORD]: Lang.t('systemLog.operationChangePassword'),
    [SystemLogOperation.IMPORT]: Lang.t('systemLog.operationImport'),
    [SystemLogOperation.EXPORT]: Lang.t('systemLog.operationExport'),
    [SystemLogOperation.BACKUP]: Lang.t('systemLog.operationBackup'),
    [SystemLogOperation.RESTORE]: Lang.t('systemLog.operationRestore'),
    [SystemLogOperation.CLEAN]: Lang.t('systemLog.operationClean'),
    [SystemLogOperation.CONFIG_UPDATE]: Lang.t('systemLog.operationConfigUpdate'),
    [SystemLogOperation.ROLE_ASSIGN]: Lang.t('systemLog.operationRoleAssign'),
    [SystemLogOperation.PERMISSION_GRANT]: Lang.t('systemLog.operationPermissionGrant'),
    [SystemLogOperation.PERMISSION_REVOKE]: Lang.t('systemLog.operationPermissionRevoke'),
    // 添加缺失的操作类型映射
    [SystemLogOperation.BATCH_CREATE]: Lang.t('systemLog.operationBatchCreate'),
    [SystemLogOperation.BATCH_DELETE]: Lang.t('systemLog.operationBatchDelete'),
    [SystemLogOperation.BATCH_UPDATE]: Lang.t('systemLog.operationBatchUpdate'),
    [SystemLogOperation.TRANSFIGURATION]: Lang.t('systemLog.operationTransfiguration'),
    [SystemLogOperation.SCAN]: Lang.t('systemLog.operationScan'),
    [SystemLogOperation.SYNC]: Lang.t('systemLog.operationSync'),
    [SystemLogOperation.EXECUTE]: Lang.t('systemLog.operationExecute'),
  };
  return operationMap[targetOperation] || targetOperation;
}

  // 获取目标类型的中文显示
  getTargetTypeText(targetType?: string): string {
    const targetTargetType = targetType || this.targetType;
    const targetTypeMap: { [key: string]: string } = {
      [SystemLogTargetType.USER]: Lang.t('systemLog.targetTypeUser'),
      [SystemLogTargetType.DEPARTMENT]: Lang.t('systemLog.targetTypeDepartment'),
      [SystemLogTargetType.SPACE]: Lang.t('systemLog.targetTypeSpace'),
      [SystemLogTargetType.SHARE]: Lang.t('systemLog.targetTypeShare'),           // 新增
      [SystemLogTargetType.SPACE_MEMBER]: Lang.t('systemLog.targetTypeSpaceMember'), // 新增
      [SystemLogTargetType.SYSTEM]: Lang.t('systemLog.targetTypeSystem'),
      [SystemLogTargetType.ROLE]: Lang.t('systemLog.targetTypeRole'),
      [SystemLogTargetType.LOG]: Lang.t('systemLog.targetTypeLog'),
      [SystemLogTargetType.BACKUP]: Lang.t('systemLog.targetTypeBackup'),
      [SystemLogTargetType.SETTING]: Lang.t('systemLog.targetTypeSetting'),
      [SystemLogTargetType.SYSTEMTASK]: Lang.t('systemLog.targetTypeSystemTask'),   // 新增
    };
    return targetTypeMap[targetTargetType] || targetTargetType;
  }

  // 获取日志类型的中文显示
  getLogTypeText(logType?: string): string {
    const targetLogType = logType || this.logType;
    const logTypeMap: { [key: string]: string } = {
      [SystemLogType.NORMAL]: Lang.t('systemLog.logTypeNormal'),
      [SystemLogType.WARNING]: Lang.t('systemLog.logTypeWarning'),
      [SystemLogType.ERROR]: Lang.t('systemLog.logTypeError'),
      [SystemLogType.SECURITY]: Lang.t('systemLog.logTypeSecurity'),
    };
    return logTypeMap[targetLogType] || targetLogType;
  }

  // 获取报警级别的中文显示
  getAlarmLevelText(alarmLevel?: number): string {
    const targetAlarmLevel = alarmLevel !== undefined ? alarmLevel : this.alarmLevel;
    const alarmLevelMap: { [key: number]: string } = {
      [SystemLogAlarmLevel.NONE]: Lang.t('systemLog.alarmLevelNone'),
      [SystemLogAlarmLevel.WARNING]: Lang.t('systemLog.alarmLevelWarning'),
      [SystemLogAlarmLevel.ERROR]: Lang.t('systemLog.alarmLevelError'),
      [SystemLogAlarmLevel.CRITICAL]: Lang.t('systemLog.alarmLevelCritical'),
    };
    return alarmLevelMap[targetAlarmLevel] || String(targetAlarmLevel);
  }

  // 获取报警级别颜色
  getAlarmLevelColor(): string {
    switch (this.alarmLevel) {
      case SystemLogAlarmLevel.NONE:
        return 'default';
      case SystemLogAlarmLevel.WARNING:
        return 'warning';
      case SystemLogAlarmLevel.ERROR:
        return 'error';
      case SystemLogAlarmLevel.CRITICAL:
        return 'red';
      default:
        return 'default';
    }
  }

  // 获取日志类型颜色
  getLogTypeColor(): string {
    switch (this.logType) {
      case SystemLogType.NORMAL:
        return 'blue';
      case SystemLogType.WARNING:
        return 'orange';
      case SystemLogType.ERROR:
        return 'red';
      case SystemLogType.SECURITY:
        return 'green';
      default:
        return 'default';
    }
  }

  // 获取格式化后的创建时间
  getFormattedCreateTime(): string {
    return DateUtil.simpleDateTime(this.createTime);
  }

  // 获取简化的详情显示（用于表格中显示）
  getSimplifiedDetail(maxLength: number = 100): string {
    if (!this.detail) return '';
    if (this.detail.length <= maxLength) return this.detail;
    return this.detail.substring(0, maxLength) + '...';
  }

  // 获取用户显示信息
  getUserDisplay(): string {
    return this.username || this.userUuid?.substring(0, 8) + '...' || Lang.t('systemLog.unknownUser');
  }

  // 获取目标显示信息
  getTargetDisplay(): string {
    return this.targetName || this.targetUuid?.substring(0, 8) + '...' || Lang.t('systemLog.unknownTarget');
  }

  httpDetail(
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    let that = this;
    if (!this.uuid) {
      this.errorMessage = Lang.t('systemLog.uuidNotSpecified');
      this.defaultErrorHandler(this.errorMessage, errorCallback);
      return;
    }

    this.detailLoading = true;

    this.httpGet(
      this.getUrlDetail(),
      {
        uuid: this.uuid,
      },
      function (response: any) {
        that.detailLoading = false;
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.detailLoading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      },
      finallyCallback
    );
  }

  getFilters(): Filter[] {
    // 将 SystemLogModule 枚举转换为选项数组
    const moduleOptions = Object.values(SystemLogModule).map(value => ({
      value: value,
      name: this.getModuleText(value)
    }));

    // 将 SystemLogOperation 枚举转换为选项数组
    const operationOptions = Object.values(SystemLogOperation).map(value => ({
      value: value,
      name: this.getOperationText(value)
    }));

    // 将 SystemLogTargetType 枚举转换为选项数组
    const targetTypeOptions = Object.values(SystemLogTargetType).map(value => ({
      value: value,
      name: this.getTargetTypeText(value)
    }));

    // 将 SystemLogType 枚举转换为选项数组
    const logTypeOptions = Object.values(SystemLogType).map(value => ({
      value: value,
      name: this.getLogTypeText(value)
    }));

    // 报警级别选项
    const alarmLevelOptions = [
      { value: SystemLogAlarmLevel.NONE.toString(), name: this.getAlarmLevelText(SystemLogAlarmLevel.NONE) },
      { value: SystemLogAlarmLevel.WARNING.toString(), name: this.getAlarmLevelText(SystemLogAlarmLevel.WARNING) },
      { value: SystemLogAlarmLevel.ERROR.toString(), name: this.getAlarmLevelText(SystemLogAlarmLevel.ERROR) },
      { value: SystemLogAlarmLevel.CRITICAL.toString(), name: this.getAlarmLevelText(SystemLogAlarmLevel.CRITICAL) },
    ];

    return [
      ...super.getFilters(),
      new InputFilter(Lang.t('user.username'), 'username', Lang.t('user.usernamesearch')),
      new SelectionFilter(Lang.t('systemLog.module'), 'module', moduleOptions),
      new SelectionFilter(Lang.t('systemLog.operation'), 'operation', operationOptions),
      new SelectionFilter(Lang.t('systemLog.targetType'), 'targetType', targetTypeOptions),
      new InputFilter(Lang.t('systemLog.targetName'), 'targetName'),
      new SelectionFilter(Lang.t('systemLog.logType'), 'logType', logTypeOptions),
      new SelectionFilter(Lang.t('systemLog.alarmLevel'), 'alarmLevel', alarmLevelOptions),
      new InputFilter('IP', 'ip'),
      new InputFilter(Lang.t('systemLog.keyword'), 'keyword', Lang.t('systemLog.keywordPlaceholder')),
      new SortFilter(Lang.t('createTime'), 'orderCreateTime'),
    ];
  }

  getUrlPrefix() {
    return '/api/system/log/';
  }

  // 获取列表URL
  getUrlList(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}page`;
  }

  // 获取详情URL
  getUrlDetail(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}detail`;
  }

  // 获取导出URL
  getUrlExport(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}export`;
  }

  // 获取清理URL
  getUrlClean(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}clean`;
  }

  // 获取统计URL
  getUrlStatistics(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}statistics`;
  }

  // 获取模块列表URL
  getUrlModules(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}modules`;
  }

  // 获取操作类型列表URL
  getUrlOperations(): string {
    return `${EnvUtil.currentHost()}${this.getUrlPrefix()}operations`;
  }

  // 导出系统日志
  httpExport(params: any = {}, successCallback?: any, errorCallback?: any) {
    let that = this;
    this.loading = true;

    // 构建查询字符串
    const queryString = Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== '')
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    const url = `${this.getUrlExport()}${queryString ? '?' + queryString : ''}`;

    // 使用GET请求下载文件
    window.location.href = url;

    // 模拟回调（实际上下载是浏览器行为）
    setTimeout(() => {
      that.loading = false;
      if (successCallback) {
        successCallback();
      }
    }, 1000);
  }

  // 清理系统日志
  httpClean(keepDays: number = 90, successCallback?: any, errorCallback?: any) {
    let that = this;
    this.loading = true;

    this.httpPost(
      this.getUrlClean(),
      { keepDays },
      function (response: any) {
        that.loading = false;
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.loading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      }
    );
  }

  // 获取统计信息
  httpStatistics(days: number = 30, successCallback?: any, errorCallback?: any) {
    let that = this;
    this.loading = true;

    this.httpGet(
      this.getUrlStatistics(),
      { days },
      function (response: any) {
        that.loading = false;
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.loading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      }
    );
  }

  // 获取模块列表
  httpModules(successCallback?: any, errorCallback?: any) {
    let that = this;
    this.loading = true;

    this.httpGet(
      this.getUrlModules(),
      {},
      function (response: any) {
        that.loading = false;
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.loading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      }
    );
  }

  // 获取操作类型列表
  httpOperations(successCallback?: any, errorCallback?: any) {
    let that = this;
    this.loading = true;

    this.httpGet(
      this.getUrlOperations(),
      {},
      function (response: any) {
        that.loading = false;
        SafeUtil.safeCallback(successCallback)(response);
      },
      function (response: any) {
        that.loading = false;
        if (typeof errorCallback === 'function') {
          errorCallback(that.getErrorMessage(response), response);
        } else {
          that.defaultErrorHandler(response);
        }
      }
    );
  }

  // 验证过滤器
  validateFilter(): boolean {
    // 这里可以添加特定的验证逻辑
    return true;
  }

  // 静态方法：获取所有模块选项（用于下拉选择）
  static getModuleOptions(): { value: string; label: string }[] {
    return Object.values(SystemLogModule).map(value => ({
      value: value,
      label: new SystemLog().getModuleText(value)
    }));
  }

  // 静态方法：获取所有操作类型选项（用于下拉选择）
  static getOperationOptions(): { value: string; label: string }[] {
    return Object.values(SystemLogOperation).map(value => ({
      value: value,
      label: new SystemLog().getOperationText(value)
    }));
  }

  // 静态方法：获取所有目标类型选项（用于下拉选择）
  static getTargetTypeOptions(): { value: string; label: string }[] {
    return Object.values(SystemLogTargetType).map(value => ({
      value: value,
      label: new SystemLog().getTargetTypeText(value)
    }));
  }

  // 静态方法：获取所有日志类型选项（用于下拉选择）
  static getLogTypeOptions(): { value: string; label: string }[] {
    return Object.values(SystemLogType).map(value => ({
      value: value,
      label: new SystemLog().getLogTypeText(value)
    }));
  }

  // 静态方法：获取所有报警级别选项（用于下拉选择）
  static getAlarmLevelOptions(): { value: string; label: string }[] {
    return [
      { value: SystemLogAlarmLevel.NONE.toString(), label: Lang.t('systemLog.alarmLevelNone') },
      { value: SystemLogAlarmLevel.WARNING.toString(), label: Lang.t('systemLog.alarmLevelWarning') },
      { value: SystemLogAlarmLevel.ERROR.toString(), label: Lang.t('systemLog.alarmLevelError') },
      { value: SystemLogAlarmLevel.CRITICAL.toString(), label: Lang.t('systemLog.alarmLevelCritical') },
    ];
  }

  // 获取模块图标类名（用于前端显示）
  getModuleIconClass(): string {
    const iconMap: { [key: string]: string } = {
      [SystemLogModule.AUTH]: 'anticon-safety-certificate',
      [SystemLogModule.USER]: 'anticon-user',
      [SystemLogModule.DEPARTMENT]: 'anticon-team',
      [SystemLogModule.SPACE]: 'anticon-dashboard',
      [SystemLogModule.SYSTEM]: 'anticon-setting',
      [SystemLogModule.ROLE]: 'anticon-safety-certificate',
      [SystemLogModule.LOG]: 'anticon-eye',
      [SystemLogModule.BACKUP]: 'anticon-download',
      [SystemLogModule.ALARM]: 'anticon-warning',
      [SystemLogOperation.BATCH_CREATE]: 'anticon-plus-square',
      [SystemLogOperation.BATCH_DELETE]: 'anticon-delete',
      [SystemLogOperation.BATCH_UPDATE]: 'anticon-edit',
      [SystemLogOperation.TRANSFIGURATION]: 'anticon-retweet',
      [SystemLogOperation.SCAN]: 'anticon-scan',
      [SystemLogOperation.SYNC]: 'anticon-sync',
      [SystemLogOperation.EXECUTE]: 'anticon-play-circle',
    };
    return iconMap[this.module] || 'anticon-info-circle';
  }

  // 获取操作图标类名（用于前端显示）
  getOperationIconClass(): string {
    const iconMap: { [key: string]: string } = {
      [SystemLogOperation.LOGIN]: 'anticon-user',
      [SystemLogOperation.LOGOUT]: 'anticon-user',
      [SystemLogOperation.LOGIN_FAILED]: 'anticon-warning',
      [SystemLogOperation.CREATE]: 'anticon-plus-circle',
      [SystemLogOperation.UPDATE]: 'anticon-edit',
      [SystemLogOperation.DELETE]: 'anticon-delete',
      [SystemLogOperation.ENABLE]: 'anticon-check-circle',
      [SystemLogOperation.DISABLE]: 'anticon-close-circle',
      [SystemLogOperation.RESET_PASSWORD]: 'anticon-safety-certificate',
      [SystemLogOperation.CONFIG_UPDATE]: 'anticon-setting',
      [SystemLogOperation.IMPORT]: 'anticon-upload',
      [SystemLogOperation.EXPORT]: 'anticon-download',
      [SystemLogOperation.BACKUP]: 'anticon-database',
      [SystemLogOperation.RESTORE]: 'anticon-rollback',
      [SystemLogOperation.CLEAN]: 'anticon-delete',
    };
    return iconMap[this.operation] || 'anticon-info-circle';
  }
}